/**
 * Single source of truth for URL parameters shared across the canvas.
 * Route-specific callback parameters (OAuth, billing, installer) stay with
 * their owning routes instead of being treated as persistent canvas state.
 */

export const CANVAS_URL_PARAMS = {
  pipeline: "pipeline",
  scanMode: "scan_mode",
  prNumber: "PR",
  useCache: "useCache",
  visibility: "visibility",
} as const;

const CODE_CONNECTION_EVENT = "code_connect";
const ANALYZE_ENDPOINT = "/api/analyze";
const FULL_VISIBILITY = "full";

export const getCanvasPrNumber = (search: string): number | null => {
  const rawPrNumber = new URLSearchParams(search).get(CANVAS_URL_PARAMS.prNumber);
  const parsedPrNumber = rawPrNumber ? Number(rawPrNumber) : NaN;
  return Number.isInteger(parsedPrNumber) && parsedPrNumber > 0 ? parsedPrNumber : null;
};

export const setCanvasPrNumber = (search: string, prNumber: number | null): string => {
  const searchParams = new URLSearchParams(search);
  const normalizedPrNumber = prNumber && prNumber > 0 ? prNumber : null;

  if (normalizedPrNumber === null) {
    searchParams.delete(CANVAS_URL_PARAMS.prNumber);
  } else {
    searchParams.set(CANVAS_URL_PARAMS.prNumber, String(normalizedPrNumber));
  }

  return searchParams.toString();
};

export const getCanvasUseCache = (search: string): boolean | undefined => {
  const rawUseCache = new URLSearchParams(search).get(CANVAS_URL_PARAMS.useCache);
  return rawUseCache === null ? undefined : rawUseCache === "true";
};

export const isFullCanvasVisibility = (search: string): boolean => {
  return new URLSearchParams(search).get(CANVAS_URL_PARAMS.visibility) === FULL_VISIBILITY;
};

export const setFullCanvasVisibility = (search: string): string => {
  const searchParams = new URLSearchParams(search);
  searchParams.set(CANVAS_URL_PARAMS.visibility, FULL_VISIBILITY);
  return searchParams.toString();
};

export const getPipeline = (search: string): string | undefined => {
  return new URLSearchParams(search).get(CANVAS_URL_PARAMS.pipeline) ?? undefined;
};

export const isV3Pipeline = (search: string): boolean =>
  getPipeline(search)?.toLowerCase() === "v3";

export const addPipelineToSocketPayload = (
  event: string,
  payload: unknown,
  search: string,
): unknown => {
  const pipeline = getPipeline(search);
  if (event !== CODE_CONNECTION_EVENT || pipeline === undefined) {
    return payload;
  }

  return {
    ...(typeof payload === "object" && payload !== null ? payload : {}),
    pipeline,
  };
};

export const addPipelineToAnalyzeRequest = (
  method: string | undefined,
  url: string | undefined,
  params: Record<string, unknown> | undefined,
  search: string,
): Record<string, unknown> | undefined => {
  const pipeline = getPipeline(search);
  if (method?.toLowerCase() !== "post" || url !== ANALYZE_ENDPOINT || pipeline === undefined) {
    return params;
  }

  return {
    ...params,
    pipeline,
  };
};
